import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "../styles/EthnicityAnalysis.css";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function TableNo35() {
  const pdfRef = useRef();

  // 📊 Employment Data by Ethnicity
  const employmentData = [
    {
      ethnicityLabel: "All Ethnicities",
      emp2018: 117738,
      emp2019: 121701,
      emp2020: 120456,
      emp2021: 129174,
      emp2022: 136688,
      emp2023: 136704,
      emp2024: 138061,
    },
    {
      ethnicityLabel: "Hispanic or Latino",
      emp2018: 21905,
      emp2019: 23104,
      emp2020: 23265,
      emp2021: 25011,
      emp2022: 27436,
      emp2023: 28165,
      emp2024: 29595,
    },
  ];

  // 🧮 Growth calculation (2018–2024)
  const dataWithChange = employmentData.map((item) => ({
    ...item,
    change: (item.emp2024 - item.emp2018).toFixed(2),
  }));

  const highest = dataWithChange.reduce((a, b) =>
    parseFloat(a.change) > parseFloat(b.change) ? a : b,
  );
  const lowest = dataWithChange.reduce((a, b) =>
    parseFloat(a.change) < parseFloat(b.change) ? a : b,
  );

  // 📈 Chart data
  const chartData = {
    labels: employmentData.map((item) => item.ethnicityLabel),
    datasets: [
      {
        label: "2018",
        data: employmentData.map((i) => i.emp2018),
        backgroundColor: "#FFF085",
      },
      {
        label: "2019",
        data: employmentData.map((i) => i.emp2019),
        backgroundColor: "#F6CFFF",
      },
      {
        label: "2020",
        data: employmentData.map((i) => i.emp2020),
        backgroundColor: "#2C92B8",
      },
      {
        label: "2021",
        data: employmentData.map((i) => i.emp2021),
        backgroundColor: "#9F9FA9",
      },
      {
        label: "2022",
        data: employmentData.map((i) => i.emp2022),
        backgroundColor: "#E9D4FF",
      },
      {
        label: "2023",
        data: employmentData.map((i) => i.emp2023),
        backgroundColor: "#D8F999",
      },
      {
        label: "2024",
        data: employmentData.map((i) => i.emp2024),
        backgroundColor: "#82181A",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "👥 Employment Count by Ethnicity (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${ctx.raw.toLocaleString("en-US")} employees`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: "#4B5563", callback: (v) => v.toLocaleString("en-US") },
        title: {
          display: true,
          text: "Employment Count",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Ethnicity Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // 📄 PDF Export
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text(APP_VERSION, 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );
        pdf.save("employment_by_ethnicity.pdf");
        // input.style.width = originalWidth;
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          Employment Count by Ethnicity (2018–2024)
        </h2>
        <p className="text-center text-gray-600 mb-6">
          Comparison of total and Hispanic or Latino workforce employment trends
          in Utah, highlighting growth between 2018 and 2024.
        </p>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Growth</p>
            <p className="text-lg font-bold text-green-700">
              {highest.ethnicityLabel} (+{highest.change})
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Lowest Growth</p>
            <p className="text-lg font-bold text-red-700">
              {lowest.ethnicityLabel} ({lowest.change})
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2 text-left">Ethnicity</th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2018
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2019
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2020
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2021
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2022
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2023
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    2024
                  </th>
                  <th className="p-2" style={{ textAlign: "right" }}>
                    Δ 2018–2024
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataWithChange.map((row, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-100 transition bg-white even:bg-gray-50"
                  >
                    <td className="p-2 font-medium text-left">
                      {row.ethnicityLabel}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2018.toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2019.toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2020.toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2021.toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2022.toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2023.toLocaleString("en-US")}
                    </td>
                    <td
                      className="p-2"
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.emp2024.toLocaleString("en-US")}
                    </td>
                    <td
                      className={`p-2 font-semibold ${
                        row.change > 0 ? "text-green-700" : "text-red-700"
                      }`}
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.change > 0 ? "+" : ""}
                      {row.change}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
            <div
              className="x-axis-legend"
              style={{
                marginTop: "10px",
                padding: "10px",
                backgroundColor: "#f9fafb",
                borderLeft: "4px solid #014cb2",
                fontSize: "14px",
                color: "#6b7280",
                marginLeft: "16px",
                marginRight: "16px",
                marginBottom: "16px",
              }}
            >
              <strong>X-axis Legend:</strong> The chart displays ethnicity
              groups (All Ethnicities, Hispanic or Latino) on the x-axis, with
              each bar representing the employment count for a specific year
              from 2018 to 2024.
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          📊 Between 2018 and 2024,{" "}
          <span className="font-semibold text-green-700">
            {highest.ethnicityLabel}
          </span>{" "}
          recorded the largest employment increase (+{highest.change}), while{" "}
          <span className="font-semibold text-red-700">
            {lowest.ethnicityLabel}
          </span>{" "}
          experienced the smallest growth ({lowest.change}).
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
